import { refreshList } from "./refresh";

const SUBMIT_DELAY_MS = 450;
let submitTimer: number | undefined;

const listForm = (): HTMLFormElement | null => document.querySelector<HTMLFormElement>("[data-cs-list-form]");
const filterGroup = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-cs-filters]");

function addHidden(form: HTMLFormElement, name: string, value: string): void {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value;
  input.dataset.csTransient = "true";
  form.appendChild(input);
}

// The panel and its expanded rows re-render open after the reload (?panel=open&fsec[]=…).
function submitSoon(keepPanelOpen: boolean): void {
  const form = listForm();
  if (!form) return;

  window.clearTimeout(submitTimer);
  submitTimer = window.setTimeout(async () => {
    form.querySelectorAll("[data-cs-transient]").forEach((input) => input.remove());
    if (keepPanelOpen) {
      addHidden(form, "panel", "open");
      form.querySelectorAll<HTMLElement>(".fg-sec.open[data-cs-fsec]").forEach((sec) => addHidden(form, "fsec[]", sec.dataset.csFsec || ""));
    }
    const url = new URL(form.action, window.location.origin);
    new FormData(form).forEach((value, key) => { if (String(value) !== "") url.searchParams.append(key, String(value)); });
    if (!(await refreshList(url.toString(), true))) form.requestSubmit();
    if (keepPanelOpen) fitToViewport(document.querySelector<HTMLElement>("[data-cs-filters-panel]"));
  }, SUBMIT_DELAY_MS);
}

// The page itself doesn't scroll (only the table does), so a popup is capped to the space left below it.
const VIEWPORT_GAP_PX = 16;
function fitToViewport(el: HTMLElement | null): void {
  if (!el) return;
  el.style.maxHeight = "";
  const room = window.innerHeight - el.getBoundingClientRect().top - VIEWPORT_GAP_PX;
  if (el.scrollHeight > room) el.style.maxHeight = `${Math.max(room, 160)}px`;
}

function setPanelOpen(open: boolean): void {
  const group = filterGroup();
  const panel = document.querySelector<HTMLElement>("[data-cs-filters-panel]");
  if (!group || !panel) return;

  panel.hidden = !open;
  group.classList.toggle("open", open);
  if (open) fitToViewport(panel);
  group.querySelector("[data-cs-filters-toggle]")?.setAttribute("aria-expanded", String(open));
}

function closeDropdowns(except?: Element | null): void {
  document.querySelectorAll<HTMLElement>("[data-cs-dd].open").forEach((dd) => {
    if (dd === except) return;
    dd.classList.remove("open");
    dd.querySelector(".dd-trigger")?.setAttribute("aria-expanded", "false");
  });
}

function syncOptions(menu: Element): void {
  const boxes = Array.from(menu.querySelectorAll<HTMLInputElement>("input[type=checkbox]"));
  boxes.forEach((box) => box.closest(".dd-opt")?.classList.toggle("active", box.checked));
  menu.querySelector("[data-cs-opt-all]")?.classList.toggle("active", !boxes.some((box) => box.checked));
}

export function bindCsListFilters(): void {
  // A panel re-rendered open after a filter change is fitted once the page has loaded.
  document.addEventListener("turbo:load", () => {
    const panel = document.querySelector<HTMLElement>("[data-cs-filters-panel]");
    if (panel && !panel.hidden) fitToViewport(panel);
  });
  window.addEventListener("resize", () => {
    const panel = document.querySelector<HTMLElement>("[data-cs-filters-panel]");
    if (panel && !panel.hidden) fitToViewport(panel);
  });

  document.addEventListener("change", (e) => {
    const target = e.target as HTMLInputElement;
    if (!target.matches("[data-cs-autosubmit]") || !target.closest("[data-cs-list-form]")) return;

    const menu = target.closest(".dd-menu");
    if (menu) syncOptions(menu);
    submitSoon(Boolean(target.closest("[data-cs-filters-panel]")));
  });

  document.addEventListener("input", (e) => {
    if ((e.target as HTMLElement).matches("[data-cs-search]")) submitSoon(false);
  });

  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (!document.querySelector("[data-cs-list]")) return;

    const all = target.closest<HTMLElement>("[data-cs-opt-all]");
    if (all) {
      const menu = all.closest(".dd-menu");
      if (!menu || all.classList.contains("active")) return;
      menu.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach((box) => { box.checked = false; });
      syncOptions(menu);
      submitSoon(Boolean(all.closest("[data-cs-filters-panel]")));
      return;
    }

    const sectionToggle = target.closest("[data-cs-fsec-toggle]");
    if (sectionToggle) {
      sectionToggle.closest(".fg-sec")?.classList.toggle("open");
      fitToViewport(document.querySelector<HTMLElement>("[data-cs-filters-panel]"));
      return;
    }

    const ddTrigger = target.closest("[data-cs-dd] > .dd-trigger");
    if (ddTrigger) {
      const dd = ddTrigger.parentElement;
      closeDropdowns(dd);
      setPanelOpen(false);
      const open = dd?.classList.toggle("open") || false;
      ddTrigger.setAttribute("aria-expanded", String(open));
      if (open) fitToViewport(dd?.querySelector<HTMLElement>(".dd-menu") || null);
      return;
    }

    if (target.closest("[data-cs-filters-toggle]")) {
      closeDropdowns();
      setPanelOpen(!filterGroup()?.classList.contains("open"));
      return;
    }

    if (!target.closest("[data-cs-dd]")) closeDropdowns();
    if (!target.closest("[data-cs-filters]")) setPanelOpen(false);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    closeDropdowns();
    setPanelOpen(false);
  });
}
